/*
 * schema.js — Portable JSON-LD structured data builder
 * ----------------------------------------------------------------------------
 * Reads a server-rendered data island (window.__SEO, emitted by
 * snippets/schema-data.liquid) and injects a single <script type="application/
 * ld+json"> containing a schema.org @graph into <head>.
 *
 * Design goals:
 *   - Zero dependencies, no build step (loaded as a plain <script src> asset).
 *   - One combined @graph node so entities can cross-reference by @id.
 *   - Client-agnostic: all site-specific values come from window.__SEO, so the
 *     same file can be reused across Shopify clients unchanged.
 *   - Non-destructive: each builder is gated by window.__SEO.config.enabled so
 *     you can turn types on one at a time as you retire legacy Liquid blocks
 *     (prevents duplicate Product/FAQ/Article markup during migration).
 *
 * Modern syntax (const/let/arrow fns) is fine here: JSON-LD injection only
 * benefits crawlers that execute JS (Googlebot runs evergreen Chromium), and
 * every browser Shopify supports handles ES2017+.
 *
 * To port to another client: copy this file + schema-data.liquid, then edit
 * only the Liquid snippet. This file rarely needs to change.
 * ----------------------------------------------------------------------------
 */
(() => {
  'use strict';

  const SEO = window.__SEO;
  if (!SEO || typeof SEO !== 'object') return;

  const cfg = SEO.config || {};
  const enabled = cfg.enabled || {};
  const org = SEO.org || {};

  // --- helpers --------------------------------------------------------------

  // Default OFF: a type must be explicitly enabled in the data island.
  const isOn = (type) => enabled[type] === true;

  const absoluteUrl = (path) => {
    if (!path) return undefined;
    if (/^https?:\/\//i.test(path)) return path;
    const base = (org.url || window.location.origin || '').replace(/\/$/, '');
    return base + (path.charAt(0) === '/' ? '' : '/') + path;
  };

  const isEmpty = (v) =>
    v === undefined || v === null || (typeof v === 'string' && v === '');

  // Strip null/undefined/empty values so we never emit half-populated nodes.
  const clean = (obj) => {
    if (Array.isArray(obj)) {
      const arr = obj.map(clean).filter((v) => !isEmpty(v));
      return arr.length ? arr : undefined;
    }
    if (obj && typeof obj === 'object' && !obj._raw) {
      const out = Object.fromEntries(
        Object.entries(obj)
          .map(([k, v]) => [k, clean(v)])
          .filter(([, v]) => !isEmpty(v))
      );
      return Object.keys(out).length ? out : undefined;
    }
    return obj;
  };

  const id = (fragment) => (org.url || '').replace(/\/$/, '') + '/#' + fragment;

  // --- entity builders ------------------------------------------------------
  // Each returns a schema.org node (object) or undefined to skip.

  const buildOrganization = () => {
    if (!isOn('organization')) return undefined;
    return {
      '@type': 'Organization',
      '@id': id('organization'),
      name: org.name,
      url: org.url,
      logo: org.logo
        ? { '@type': 'ImageObject', url: absoluteUrl(org.logo) }
        : undefined,
      sameAs: org.sameAs, // array of social profile URLs
    };
  };

  const buildWebSite = () => {
    if (!isOn('website')) return undefined;
    const node = {
      '@type': 'WebSite',
      '@id': id('website'),
      name: org.name,
      url: org.url,
      publisher: { '@id': id('organization') },
    };
    if (org.searchUrl) {
      // Enables the Google sitelinks search box.
      node.potentialAction = {
        '@type': 'SearchAction',
        target: {
          '@type': 'EntryPoint',
          urlTemplate: org.searchUrl + '{search_term_string}',
        },
        'query-input': 'required name=search_term_string',
      };
    }
    return node;
  };

  const buildBreadcrumb = () => {
    if (!isOn('breadcrumb')) return undefined;
    const items = SEO.breadcrumbs;
    if (!items || !items.length) return undefined;
    return {
      '@type': 'BreadcrumbList',
      itemListElement: items.map((item, i) => ({
        '@type': 'ListItem',
        position: i + 1,
        name: item.name,
        item: absoluteUrl(item.url),
      })),
    };
  };

  const buildProduct = () => {
    if (!isOn('product')) return undefined;
    const p = SEO.product;
    if (!p) return undefined;

    const offers = (p.variants || []).map((v) => ({
      '@type': 'Offer',
      sku: v.sku,
      price: v.price, // already formatted as a decimal string by Liquid
      priceCurrency: p.currency,
      availability: 'https://schema.org/' + (v.available ? 'InStock' : 'OutOfStock'),
      priceValidUntil: p.priceValidUntil,
      url: absoluteUrl(v.url),
    }));

    const node = {
      '@type': 'Product',
      '@id': absoluteUrl(p.url) + '#product',
      name: p.title,
      description: p.description,
      sku: p.sku,
      url: absoluteUrl(p.url),
      brand: p.brand ? { '@type': 'Brand', name: p.brand } : undefined,
      image: (p.images || []).map(absoluteUrl),
      offers:
        offers.length > 1
          ? {
              '@type': 'AggregateOffer',
              offerCount: offers.length,
              lowPrice: p.priceMin,
              highPrice: p.priceMax,
              priceCurrency: p.currency,
              offers,
            }
          : offers[0],
    };

    // GTIN / MPN — only emit when a valid barcode is present.
    if (p.gtin) node[p.gtinType || 'gtin'] = p.gtin;
    if (p.mpn) node.mpn = p.mpn;

    // AggregateRating / Review — only when a reviews source supplied them.
    if (p.rating && p.ratingCount) {
      node.aggregateRating = {
        '@type': 'AggregateRating',
        ratingValue: p.rating,
        reviewCount: p.ratingCount,
      };
    }

    return node;
  };

  const buildCollection = () => {
    if (!isOn('collection')) return undefined;
    const c = SEO.collection;
    if (!c) return undefined;
    const node = {
      '@type': 'CollectionPage',
      name: c.title,
      description: c.description,
      url: absoluteUrl(c.url),
    };
    if (c.products && c.products.length) {
      node.mainEntity = {
        '@type': 'ItemList',
        numberOfItems: c.products.length,
        itemListElement: c.products.map((pr, i) => ({
          '@type': 'ListItem',
          position: i + 1,
          url: absoluteUrl(pr.url),
          name: pr.title,
        })),
      };
    }
    return node;
  };

  const buildArticle = () => {
    if (!isOn('article')) return undefined;
    const a = SEO.article;
    if (!a) return undefined;
    return {
      '@type': 'BlogPosting',
      headline: a.title,
      description: a.excerpt,
      image: a.image ? absoluteUrl(a.image) : undefined,
      datePublished: a.datePublished,
      dateModified: a.dateModified,
      url: absoluteUrl(a.url),
      author: a.author ? { '@type': 'Person', name: a.author } : undefined,
      publisher: { '@id': id('organization') },
      mainEntityOfPage: { '@type': 'WebPage', '@id': absoluteUrl(a.url) },
    };
  };

  const buildFaq = () => {
    if (!isOn('faq')) return undefined;
    const faqs = SEO.faqs;
    if (!faqs || !faqs.length) return undefined;
    return {
      '@type': 'FAQPage',
      mainEntity: faqs.map((f) => ({
        '@type': 'Question',
        name: f.question,
        acceptedAnswer: { '@type': 'Answer', text: f.answer },
      })),
    };
  };

  // --- Layer 2: add extra schema (metaobject-driven, SEO self-serve) ---------
  // schema-data.liquid emits SEO.custom = [{ type, ...fields }] from schema
  // metaobjects an SEO attaches to a page. This is the escape hatch for any
  // schema.org type NOT already wired up above (Organization, WebSite,
  // Breadcrumb, Product, Collection, Article, FAQ).
  //
  // Primary mechanism — raw passthrough: type 'RawJsonLd' carries a `raw` string
  // the SEO pasted verbatim. Parsed defensively — a malformed paste is skipped
  // (logged when config.debug), never crashes the page and never corrupts the
  // rest of the @graph. No validation: garbage in = silently ignored by crawlers.
  //
  // Optional future extension — curated types: to give a specific type friendly
  // labeled fields (no schema.org knowledge needed), add a mapper below keyed by
  // its @type + one `when` branch in schema-data.liquid. Empty by design today.
  const CUSTOM_BUILDERS = {
    // e.g. VideoObject: (b) => ({ '@type': 'VideoObject', name: b.name, ... }),
  };

  // Returns a flat array of nodes (or []). No enable flag: an attached, published
  // metaobject IS the opt-in. Unknown curated types are skipped, not errored.
  const buildCustom = () => {
    const blocks = SEO.custom;
    if (!Array.isArray(blocks) || !blocks.length) return [];
    const out = [];
    for (const b of blocks) {
      if (!b || !b.type) continue;

      // Layer 3 — raw pasted JSON-LD.
      if (b.type === 'RawJsonLd') {
        if (!b.raw) continue;
        try {
          const parsed = JSON.parse(b.raw);
          // Accept a single node, an array of nodes, or a full {@graph:[...]}.
          const nodes = Array.isArray(parsed)
            ? parsed
            : Array.isArray(parsed && parsed['@graph'])
            ? parsed['@graph']
            : [parsed];
          nodes.forEach((n) => {
            if (n && typeof n === 'object') {
              // The outer graph supplies @context; drop any per-node one.
              delete n['@context'];
              out.push(n);
            }
          });
        } catch (e) {
          if (cfg.debug) {
            // eslint-disable-next-line no-console
            console.warn('[schema.js] skipped invalid raw JSON-LD block:', e);
          }
        }
        continue;
      }

      // Layer 2 — curated type.
      const mapper = CUSTOM_BUILDERS[b.type];
      if (mapper) out.push(mapper(b));
    }
    return out;
  };

  // --- assemble + inject ----------------------------------------------------

  const build = () => {
    const nodes = [
      buildOrganization(),
      buildWebSite(),
      buildBreadcrumb(),
      buildProduct(),
      buildCollection(),
      buildArticle(),
      buildFaq(),
      ...buildCustom(),
    ]
      .map(clean)
      .filter(Boolean);

    if (!nodes.length) return;

    const graph = { '@context': 'https://schema.org', '@graph': nodes };

    const el = document.createElement('script');
    el.type = 'application/ld+json';
    el.setAttribute('data-schema', 'auto');
    el.text = JSON.stringify(graph);
    document.head.appendChild(el);

    if (cfg.debug) {
      // eslint-disable-next-line no-console
      console.log('[schema.js] injected @graph', graph);
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', build);
  } else {
    build();
  }
})();
